import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_INFO = {
  activo: { label: '● Activo', cls: 'bg-emerald-500/15 text-emerald-300' },
  saldo_bajo: { label: '⚠ Saldo bajo', cls: 'bg-amber-500/15 text-amber-300' },
  servicios_pausados: { label: '⛔ Servicios pausados', cls: 'bg-rose-500/15 text-rose-300' },
};

export default function PortalClientePage() {
  const [key, setKey] = useState(() => localStorage.getItem('n369_portal_key') || '');
  const [login, setLogin] = useState({ email: '', access_code: '' });
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [videos, setVideos] = useState([]);
  const [gov, setGov] = useState(null);
  const [tab, setTab] = useState('consumo');

  const pFetch = useCallback((url) => fetch(url, { headers: { 'X-Portal-Key': key } }), [key]);

  const loadAll = useCallback(async () => {
    try {
      const me = await pFetch(`${API}/portal/me`);
      if (!me.ok) { setKey(''); localStorage.removeItem('n369_portal_key'); return; }
      setClient((await me.json()).client);
      const [c1, c2, c3, c4, c5] = await Promise.all([pFetch(`${API}/portal/consumption`), pFetch(`${API}/portal/contracts`), pFetch(`${API}/portal/invoices`), pFetch(`${API}/portal/monthly-invoices`), pFetch(`${API}/portal/videos`)]);
      if (c1.ok) setConsumption(await c1.json());
      if (c2.ok) setContracts((await c2.json()).contracts || []);
      if (c3.ok) setInvoices((await c3.json()).invoices || []);
      if (c4.ok) setMonthly((await c4.json()).monthly_invoices || []);
      if (c5.ok) setVideos((await c5.json()).videos || []);
      const g = await pFetch(`${API}/portal/governance`);
      if (g.ok) setGov(await g.json());
    } catch { /* noop */ }
  }, [pFetch]);

  useEffect(() => {
    if (!key) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('recarga') === 'success' && params.get('session_id')) {
      pFetch(`${API}/portal/recharge-status?session_id=${params.get('session_id')}`).finally(() => {
        window.history.replaceState({}, '', '/portal');
        loadAll();
      });
    }
    loadAll();
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, [key, loadAll, pFetch]);

  const saveGov = async (patch) => {
    const next = { ...gov.settings, ...patch };
    const res = await fetch(`${API}/portal/governance`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Portal-Key': key }, body: JSON.stringify(next) });
    if (res.ok) { const g = await pFetch(`${API}/portal/governance`); if (g.ok) setGov(await g.json()); }
  };

  const toggleTeam = (name) => {
    const disabled = gov.settings.disabled_teams.includes(name)
      ? gov.settings.disabled_teams.filter((t) => t !== name)
      : [...gov.settings.disabled_teams, name];
    saveGov({ disabled_teams: disabled });
  };

  const buyPack = async (packId) => {
    try {
      const res = await fetch(`${API}/portal/buy-credits`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Portal-Key': key }, body: JSON.stringify({ pack_id: packId }) });
      const data = await res.json();
      if (res.ok && data.url) window.location.href = data.url;
    } catch { /* noop */ }
  };

  const doLogin = async (e) => {
    e.preventDefault(); setError(null);
    try {
      const res = await fetch(`${API}/portal/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(login) });
      const data = await res.json();
      if (res.ok) { localStorage.setItem('n369_portal_key', data.portal_key); setKey(data.portal_key); }
      else setError(data.detail || 'Acceso incorrecto');
    } catch { setError('Error de conexión'); }
  };

  if (!key || !client) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#02040a] p-6" data-testid="portal-login-page">
        <form onSubmit={doLogin} className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.03] p-7">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#00d4ff]">Neuralia369</p>
          <h1 className="mt-2 text-2xl font-black text-white">Portal de clientes</h1>
          <p className="mt-1 text-xs text-slate-400">Saldo, consumo, contratos y facturas en tiempo real.</p>
          <input type="email" value={login.email} onChange={(e) => setLogin((p) => ({ ...p, email: e.target.value }))} placeholder="Tu email" className="mt-5 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white" data-testid="portal-email" required />
          <input value={login.access_code} onChange={(e) => setLogin((p) => ({ ...p, access_code: e.target.value.toUpperCase() }))} placeholder="Código de acceso" className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm tracking-widest text-white" data-testid="portal-code" required />
          {error && <p className="mt-2 rounded-lg bg-rose-500/15 px-3 py-2 text-xs text-rose-300" data-testid="portal-login-error">{error}</p>}
          <button type="submit" className="mt-4 w-full rounded-xl bg-[#00d4ff] py-3 text-sm font-black text-[#02040a]" data-testid="portal-login-btn">Entrar</button>
        </form>
      </main>
    );
  }

  const st = STATUS_INFO[client.status] || STATUS_INFO.activo;
  return (
    <main className="min-h-screen bg-[#02040a] px-4 py-8 text-slate-200" data-testid="portal-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#00d4ff]">Neuralia369 · Portal</p>
            <h1 className="mt-1 text-2xl font-black text-white">{client.name}</h1>
            {client.company && <p className="text-xs text-slate-400">{client.company}</p>}
          </div>
          <button onClick={() => { localStorage.removeItem('n369_portal_key'); setKey(''); setClient(null); }} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-bold text-slate-300" data-testid="portal-logout">Salir</button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-3xl border border-[#00d4ff]/30 bg-[#00d4ff]/[0.06] p-5">
            <p className="text-[11px] font-bold uppercase text-slate-400">Saldo en vivo</p>
            <p className="mt-1 text-4xl font-black text-[#00d4ff]" data-testid="portal-balance">{client.credits_balance}</p>
            <p className="text-xs text-slate-400">créditos (1 cr = $0.10)</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {[['basico', '500 cr · $50'], ['popular', '2000 cr · $95'], ['empresa', '10000 cr · $850']].map(([id, label]) => (
                <button key={id} onClick={() => buyPack(id)} className="rounded-full bg-[#00d4ff] px-3 py-1.5 text-[11px] font-black text-[#02040a] hover:opacity-90" data-testid={`portal-buy-${id}`}>🔋 {label}</button>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-bold uppercase text-slate-400">Estado</p>
            <p className={`mt-2 inline-block rounded-full px-3 py-1.5 text-sm font-black ${st.cls}`} data-testid="portal-status">{st.label}</p>
            {client.status === 'servicios_pausados' && <p className="mt-2 text-[11px] text-rose-300">Recarga para reanudar automáticamente tus servicios.</p>}
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-bold uppercase text-slate-400">Consumo total</p>
            <p className="mt-1 text-2xl font-black text-white" data-testid="portal-total-consumed">{consumption?.totals?.total ?? '—'} cr</p>
            <p className="text-xs text-slate-400">{consumption ? 'consumo real medido · precio final todo incluido' : ''}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          {[['consumo', 'Consumo'], ['contratos', `Contratos (${contracts.length})`], ['facturas', `Recargas/Facturas (${invoices.length})`], ['videos', `Mis Vídeos (${videos.length})`], ['gobernanza', 'Gobernanza']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={`rounded-full px-4 py-2 text-xs font-bold ${tab === id ? 'bg-[#00d4ff] text-[#02040a]' : 'bg-white/5 text-slate-400'}`} data-testid={`portal-tab-${id}`}>{label}</button>
          ))}
          <a onClick={(e) => { e.preventDefault(); pFetch(`${API}/portal/statement.pdf`).then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'extracto-neuralia369.pdf'; a.click(); }); }} href="#extracto" className="ml-auto rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-slate-300" data-testid="portal-statement-btn">📄 Extracto PDF</a>
        </div>

        {tab === 'consumo' && (
          <div className="mt-4 space-y-1.5" data-testid="portal-consumption-list">
            {(consumption?.consumptions || []).map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-bold text-white">{d.servicio}</p>
                  <p className="text-[11px] text-slate-500">{d.timestamp?.slice(0, 16).replace('T', ' ')} {d.equipo ? `· ${d.equipo}` : ''}</p>
                </div>
                <p className="text-xs text-slate-300"><b className="text-white">{d.total_descontado} cr</b> · saldo {d.saldo_restante}</p>
              </div>
            ))}
            {!consumption?.consumptions?.length && <p className="rounded-xl bg-white/[0.02] p-5 text-center text-sm text-slate-500">Aún sin consumos registrados.</p>}
          </div>
        )}
        {tab === 'contratos' && (
          <div className="mt-4 space-y-1.5" data-testid="portal-contracts-list">
            {contracts.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
                <div><p className="font-bold text-white">{c.contract_number}</p><p className="text-[11px] text-slate-500">{c.template_type} · Plan {c.plan?.name} · {c.status}</p></div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${c.pdf_url}`} target="_blank" rel="noreferrer" className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-[#00d4ff]">PDF</a>
              </div>
            ))}
            {!contracts.length && <p className="rounded-xl bg-white/[0.02] p-5 text-center text-sm text-slate-500">Sin contratos asociados a tu email todavía.</p>}
          </div>
        )}
        {tab === 'facturas' && (
          <div className="mt-4 space-y-3">
            <div data-testid="portal-monthly-invoices">
              <p className="text-xs font-bold uppercase text-slate-400">Facturas mensuales</p>
              <div className="mt-2 space-y-1.5">
                {monthly.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
                    <div><p className="font-bold text-white">{m.invoice_number}</p><p className="text-[11px] text-slate-500">{m.month} · {m.total_credits} cr = ${m.total_usd}</p></div>
                    <button onClick={() => pFetch(`${API}/portal/invoice.pdf?month=${m.month}`).then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `factura-${m.month}.pdf`; a.click(); })} className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-[#00d4ff]">PDF</button>
                  </div>
                ))}
                {!monthly.length && <p className="rounded-xl bg-white/[0.02] p-3 text-center text-xs text-slate-500">La factura mensual se genera automáticamente el día 1 con el consumo del mes anterior. También puedes descargar el mes en curso: <button onClick={() => pFetch(`${API}/portal/invoice.pdf`).then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'factura-mes-actual.pdf'; a.click(); })} className="font-bold text-[#00d4ff] underline" data-testid="portal-current-invoice">factura del mes actual (PDF)</button></p>}
              </div>
            </div>
            <p className="text-xs font-bold uppercase text-slate-400">Recargas</p>
            <div className="space-y-1.5" data-testid="portal-invoices-list">
            {invoices.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
                <div><p className="font-bold text-emerald-300">+{r.credits} créditos</p><p className="text-[11px] text-slate-500">{r.created_at?.slice(0, 16).replace('T', ' ')} {r.note ? `· ${r.note}` : ''}</p></div>
                <p className="text-xs text-slate-400">saldo tras recarga: <b className="text-white">{r.balance_after}</b></p>
              </div>
            ))}
            {!invoices.length && <p className="rounded-xl bg-white/[0.02] p-5 text-center text-sm text-slate-500">Sin recargas todavía.</p>}
            </div>
          </div>
        )}
        {tab === 'videos' && (
          <div className="mt-4 space-y-1.5" data-testid="portal-videos-list">
            {videos.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-sm">
                <div className="flex items-center gap-3">
                  {v.snapshot_url && <img src={v.snapshot_url} alt={v.title} className="h-10 w-16 rounded-lg object-cover" />}
                  <div><p className="font-bold text-white">{v.title}</p><p className="text-[11px] text-slate-500">{v.created_at?.slice(0, 16).replace('T', ' ')}{v.credits_charged ? ` · ${v.credits_charged} cr` : ''}</p></div>
                </div>
                {v.status === 'completed' && v.video_url ? (
                  <a href={v.video_url} target="_blank" rel="noreferrer" className="rounded-full bg-[#00d4ff] px-3 py-1 text-xs font-black text-[#02040a]" data-testid={`portal-video-link-${v.id}`}>▶ Ver vídeo</a>
                ) : v.status === 'failed' ? (
                  <span className="rounded-full bg-rose-500/15 px-3 py-1 text-xs font-bold text-rose-300">Falló — contacta soporte</span>
                ) : (
                  <span className="animate-pulse rounded-full bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-300">⏳ Generando…</span>
                )}
              </div>
            ))}
            {!videos.length && <p className="rounded-xl bg-white/[0.02] p-5 text-center text-sm text-slate-500">Aún no tienes vídeos. Pídele uno a Aynara por el chat: «Hazme un vídeo de campaña».</p>}
          </div>
        )}
        {tab === 'gobernanza' && gov && (
          <div className="mt-4 space-y-4" data-testid="portal-governance-panel">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-xs font-bold uppercase text-slate-400">Límites de gasto</p>
              <p className="mt-1 text-[11px] text-slate-500">Consumo este mes: <b className="text-white">{gov.month_spent} cr</b>{gov.settings.monthly_limit_credits ? ` de ${gov.settings.monthly_limit_credits} cr permitidos` : ' · sin límite fijado'}</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-[11px] text-slate-400">Límite mensual (créditos)
                  <input type="number" min="0" defaultValue={gov.settings.monthly_limit_credits || ''} onBlur={(e) => saveGov({ monthly_limit_credits: e.target.value })} placeholder="Sin límite" className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white" data-testid="portal-gov-monthly-limit" />
                </label>
                <label className="text-[11px] text-slate-400">Pedir aprobación por encima de (créditos)
                  <input type="number" min="0" defaultValue={gov.settings.approval_above_credits || ''} onBlur={(e) => saveGov({ approval_above_credits: e.target.value })} placeholder="Sin umbral" className="mt-1 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white" data-testid="portal-gov-approval-threshold" />
                </label>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-xs font-bold uppercase text-slate-400">Qué puede hacer cada equipo de agentes</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {gov.teams.map((t) => (
                  <button key={t.name} onClick={() => toggleTeam(t.name)} className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition ${t.enabled ? 'border-emerald-400/25 bg-emerald-400/[0.06]' : 'border-rose-400/25 bg-rose-500/[0.06] opacity-70'}`} data-testid={`portal-gov-team-${t.name}`}>
                    <span><b className="text-white">{t.name}</b><span className="block text-[11px] text-slate-500">{t.scope}</span></span>
                    <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black ${t.enabled ? 'bg-emerald-400/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>{t.enabled ? 'ACTIVO' : 'BLOQUEADO'}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5" data-testid="portal-gov-audit">
              <p className="text-xs font-bold uppercase text-slate-400">Log de auditoría (últimas 20 acciones)</p>
              <div className="mt-2 space-y-1">
                {(gov.audit_log || []).map((a) => (
                  <p key={a.id} className="text-[11px] text-slate-400">{a.timestamp?.slice(0, 16).replace('T', ' ')} · <span className="text-slate-200">{a.servicio}</span> {a.equipo ? `· ${a.equipo}` : ''} · <b className="text-white">{a.total_descontado} cr</b></p>
                ))}
                {!gov.audit_log?.length && <p className="text-xs text-slate-500">Sin acciones registradas todavía.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
